/**
 * Canvas label drawing.
 *
 * Every label on the map is painted into ONE `<canvas>`. The alternative — a
 * DOM node per label — costs a layout and a paint per element, so a layer
 * with a few thousand features makes panning unusable. A single canvas draws
 * the same labels in one pass and lets us do collision detection while we
 * place them.
 *
 * Nothing here touches the map engine directly: it takes already-projected
 * anchors and a 2D context, which keeps it testable and keeps the map
 * plumbing in MapViewOL.tsx's own label-overlay effect.
 */

import type { LabelAnchor } from './LabelUtils';
import { fontStackFor, type LabelClass } from './LabelTypes';

export interface LabelDrawItem {
  text: string;
  anchor: LabelAnchor;
  labelClass: LabelClass;
}

export interface LabelDrawResult {
  drawn: number;
  /** Dropped because they collided with an already-placed label. */
  suppressed: number;
  /** Not attempted because the per-frame budget ran out. */
  skipped: number;
}

interface Rect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/**
 * Upper bound on labels painted in one frame.
 *
 * Beyond this the map is unreadable anyway, so the cap protects frame rate
 * without costing anything a user would notice. Scale-dependent visibility is
 * the real answer to a dense layer; this is the backstop.
 */
export const MAX_LABELS_PER_FRAME = 1200;

/**
 * Uniform-grid collision index.
 *
 * Comparing every candidate against every placed label is O(n²), which shows up
 * as jank well before the budget above. Hashing into 128px cells means each
 * test only looks at labels that could plausibly overlap.
 */
class CollisionGrid {
  private readonly cells = new Map<string, Rect[]>();

  private static readonly CELL = 128;

  private keysFor(rect: Rect): string[] {
    const keys: string[] = [];
    const x0 = Math.floor(rect.left / CollisionGrid.CELL);
    const x1 = Math.floor(rect.right / CollisionGrid.CELL);
    const y0 = Math.floor(rect.top / CollisionGrid.CELL);
    const y1 = Math.floor(rect.bottom / CollisionGrid.CELL);
    for (let x = x0; x <= x1; x += 1) {
      for (let y = y0; y <= y1; y += 1) keys.push(`${x}:${y}`);
    }
    return keys;
  }

  collides(rect: Rect): boolean {
    for (const key of this.keysFor(rect)) {
      const bucket = this.cells.get(key);
      if (!bucket) continue;
      for (const other of bucket) {
        if (
          rect.left < other.right &&
          rect.right > other.left &&
          rect.top < other.bottom &&
          rect.bottom > other.top
        ) {
          return true;
        }
      }
    }
    return false;
  }

  add(rect: Rect): void {
    for (const key of this.keysFor(rect)) {
      const bucket = this.cells.get(key);
      if (bucket) bucket.push(rect);
      else this.cells.set(key, [rect]);
    }
  }
}

/** CSS font shorthand for a label class. */
export function fontStringFor(labelClass: LabelClass): string {
  const { fontStyle, fontWeight, fontSize, fontFamily } = labelClass.text;
  return `${fontStyle} ${fontWeight} ${fontSize}px ${fontStackFor(fontFamily)}`;
}

/**
 * Where the text box sits relative to the anchor, in the anchor's own frame.
 *
 * Line placements displace perpendicular to the line, which after rotation
 * means displacing in y — that is why they share the above/below arithmetic
 * but are separate cases from the point ones.
 */
function offsetFor(
  placement: LabelClass['placement'],
  width: number,
  height: number,
): { dx: number; dy: number } {
  const gap = 4;
  switch (placement.type) {
    case 'above':
    case 'above-line':
      return { dx: 0, dy: -(height / 2 + gap) };
    case 'below':
    case 'below-line':
      return { dx: 0, dy: height / 2 + gap };
    case 'left':
      return { dx: -(width / 2 + gap), dy: 0 };
    case 'right':
      return { dx: width / 2 + gap, dy: 0 };
    case 'center':
    case 'on-line':
    default:
      return { dx: 0, dy: 0 };
  }
}

/** Axis-aligned bounds of a rotated text box, used for collision. */
function boundsOf(centreX: number, centreY: number, width: number, height: number, angle: number): Rect {
  const cos = Math.abs(Math.cos(angle));
  const sin = Math.abs(Math.sin(angle));
  const halfWidth = (width * cos + height * sin) / 2;
  const halfHeight = (width * sin + height * cos) / 2;
  return {
    left: centreX - halfWidth,
    top: centreY - halfHeight,
    right: centreX + halfWidth,
    bottom: centreY + halfHeight,
  };
}

export interface DrawOptions {
  /** CSS pixel size of the drawing surface. */
  width: number;
  height: number;
  /** Labels whose box falls entirely outside this margin are not drawn. */
  margin?: number;
}

/**
 * Paints the labels, in the order given, and reports what happened.
 *
 * Order matters: the first item to claim a piece of the canvas keeps it, so
 * callers that want priority (a bigger feature, a higher layer) should sort
 * before calling.
 */
export function drawLabels(
  ctx: CanvasRenderingContext2D,
  items: LabelDrawItem[],
  options: DrawOptions,
): LabelDrawResult {
  const margin = options.margin ?? 32;
  const grid = new CollisionGrid();
  const result: LabelDrawResult = { drawn: 0, suppressed: 0, skipped: 0 };

  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.lineJoin = 'round';
  ctx.miterLimit = 2;

  // Measuring is the expensive part, so the font is only re-set when it
  // actually changes — items from one layer share a class and hit this once.
  let activeFont = '';

  for (const item of items) {
    if (result.drawn >= MAX_LABELS_PER_FRAME) {
      result.skipped += 1;
      continue;
    }

    const { text, anchor, labelClass } = item;
    if (!text) continue;

    const font = fontStringFor(labelClass);
    if (font !== activeFont) {
      ctx.font = font;
      activeFont = font;
    }

    const width = ctx.measureText(text).width;
    const height = labelClass.text.fontSize * 1.2;
    const angle = labelClass.placement.parallel ? anchor.angle : 0;

    const { dx, dy } = offsetFor(labelClass.placement, width, height);
    // The placement offset is in the rotated frame; the user's manual offset is
    // in screen space, so they are applied on opposite sides of the rotation.
    const rotatedX = dx * Math.cos(angle) - dy * Math.sin(angle);
    const rotatedY = dx * Math.sin(angle) + dy * Math.cos(angle);

    const centreX = anchor.x + rotatedX + labelClass.placement.offsetX;
    const centreY = anchor.y + rotatedY + labelClass.placement.offsetY;

    const bounds = boundsOf(centreX, centreY, width, height, angle);
    if (
      bounds.right < -margin ||
      bounds.bottom < -margin ||
      bounds.left > options.width + margin ||
      bounds.top > options.height + margin
    ) {
      continue;
    }

    if (labelClass.avoidOverlap) {
      if (grid.collides(bounds)) {
        result.suppressed += 1;
        continue;
      }
      grid.add(bounds);
    }

    ctx.save();
    ctx.translate(centreX, centreY);
    if (angle !== 0) ctx.rotate(angle);

    if (labelClass.halo.enabled && labelClass.halo.width > 0) {
      // strokeText draws the outline centred on the glyph edge, so the visible
      // halo is half the line width — doubling it makes the number in the UI
      // mean the radius the user actually sees.
      ctx.lineWidth = labelClass.halo.width * 2;
      ctx.strokeStyle = labelClass.halo.color;
      ctx.strokeText(text, 0, 0);
    }

    ctx.fillStyle = labelClass.text.color;
    ctx.fillText(text, 0, 0);
    ctx.restore();

    result.drawn += 1;
  }

  return result;
}
